<script setup>
  import { NLayout, NLayoutContent, NLayoutHeader, NLayoutSider, useMessage, NLayoutFooter, NButton, NIcon } from 'naive-ui'
  import NavigationMenu from '@/components/navigation-menu.vue'
  import FooterInfo from '@/components/footer-info.vue'
  import { useUserActivityWorker } from '@/composables/use-user-activity-worker'
  import { useAuthStore } from '@/stores/auth'
  import { generateStoreName } from '@/services/store-utils'
  import { useRoute, useRouter } from 'vue-router'
  import { SignOutAlt } from '@vicons/fa'
  import { log } from '@/services/log'
  import { computed, watch, ref, provide, inject } from 'vue'
  import AboutInfo from '@/components/basic/about-info.vue'
  import ResetPasswordModal from '@/components/basic/reset-password-modal.vue'
  import UploadOngoingMOdal from '@/components/basic/upload-ongoing-modal.vue'
  import RemoteCommandConfigRunModal from '@/components/basic/remote-command-config-run-modal.vue'

  const baseURL = import.meta.env.VUE_APP_API_URL
  const props = defineProps({
    layoutClass: String,
    contentClass: String,
  })
  const currentStore = ref(null)
  const currentStoreFn = ref(null)
  const router = useRouter()
  const appStoreFn = inject('appStoreFn')
  const appStore = appStoreFn()
  const navStyle = computed(() => appStore.navStyle)
  const isNavLeft = computed(() => navStyle?.value == 'left')
  const isNavTop = computed(() => navStyle?.value == 'top')

  const message = useMessage()
  const authStore = useAuthStore()
  const userDisplayName = computed(() => authStore?.user?.username ?? 'Tech User')
  const userInitial = computed(() => (userDisplayName.value ? userDisplayName.value.charAt(0).toUpperCase() : 'T'))
  const authSuccess = computed(() => authStore.authStatus === 'SUCCESS')
  const layoutClass = computed(() => props?.layoutClass ?? 'app-layout')
  const contentClass = computed(() => props?.contentClass ?? 'app-content')
  const { sendMessage } = useUserActivityWorker()
  const versionedName = generateStoreName()
  const userActivityWorkerObj = {
    token: authStore.token,
    type: 'start',
    url: `${baseURL}/user-activity/batch`,
    versionedName: versionedName,
  }
  sendMessage(userActivityWorkerObj)
  const route = useRoute()
  const name = computed(() => {
    const value = route?.path
    log('Router view key', value)
    return value
  })
  watch(authSuccess, (success) => {
    if (!success) {
      if (route?.meta?.requiresAuth) {
        message.warning('You must be authorized to access. Please login.')
        router.push('/login')
      }
    } else message.success('Login Sucessful.')
  })
  watch(
    () => route.path,
    () => {
      currentStoreFn.value = route.meta.storeFn || null
      if (typeof currentStoreFn.value === 'function') {
        currentStore.value = currentStoreFn.value()
        log('Updated store:', currentStore.value)
      } else {
        currentStore.value = null
        log('No store function found in route meta.')
      }
    },
    { immediate: true }
  )
  const appLayoutRef = ref(null)
  provide('appLayoutRef', appLayoutRef)
</script>
<template>
  <NLayout
    :ref="appLayoutRef"
    :class="layoutClass"
  >
    <NLayoutHeader
      v-if="isNavTop"
      class="top-nav-wrapper"
    >
      <NavigationMenu
        :is-nav-top="isNavTop"
        :vertical="false"
      />
    </NLayoutHeader>
    <NLayoutContent>
      <NLayout
        v-if="authSuccess"
        :has-sider="isNavLeft"
      >
        <NLayoutSider
          v-if="isNavLeft"
          :bordered="true"
          collapse-mode="width"
          :collapsed-width="50"
          width="15vw"
          :native-scrollbar="false"
          show-trigger="bar"
          :show-collapsed-content="false"
          @after-enter="appStore.showWelcomeTitle = true"
          @after-leave="appStore.showWelcomeTitle = false"
        >
          <NavigationMenu
            :is-nav-top="isNavTop"
            :vertical="true"
          />
        </NLayoutSider>
        <NLayoutContent :class="contentClass">
          <RouterView :key="name" />
        </NLayoutContent>
      </NLayout>
      <RouterView
        v-else
        :key="name"
      />
      <ResetPasswordModal></ResetPasswordModal>
      <UploadOngoingMOdal></UploadOngoingMOdal>
      <RemoteCommandConfigRunModal></RemoteCommandConfigRunModal>
      <AboutInfo></AboutInfo>
    </NLayoutContent>
    <NLayoutFooter class="bottom-nav-wrapper">
      <FooterInfo />
    </NLayoutFooter>
  </NLayout>
</template>

<style scoped>
.top-right-user-overlay {
  position: fixed;
  top: 1rem;
  right: 1.25rem;
  z-index: 1200;
  pointer-events: none;
}

.top-right-user-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(15, 23, 42, 0.95);
  color: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 9999px;
  padding: 0.6rem 0.85rem;
  min-width: 220px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.2);
  pointer-events: auto;
}

.top-right-user-initial {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #1d4ed8;
  color: #ffffff;
  font-weight: 700;
}

.top-right-user-copy {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.top-right-user-welcome {
  font-size: 0.85rem;
  color: #cbd5e1;
}

.top-right-user-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #ffffff;
}

.top-right-logout-button {
  min-width: 2.4rem;
  height: 2.4rem;
  padding: 0;
  border-radius: 9999px;
}
  .top-right-user-overlay {
    display: none;
  }

  .top-right-user-pill,
  .top-right-user-initial,
  .top-right-user-copy,
  .top-right-user-welcome,
  .top-right-user-name,
  .top-right-logout-button {
    display: none;
  }
</style>
